import { useCallback, useEffect, useState } from 'react';
import {
  usePushConfigQuery,
  useSubscribeMutation,
  useUnsubscribeMutation,
} from './api';
import {
  currentSubscription,
  ensureSubscription,
  pushSupported,
  subscriptionInput,
} from './push';

export type PushStatus = 'unsupported' | 'disabled' | 'default' | 'denied' | 'subscribed';

/**
 * Owns this browser's push registration.
 *
 * The server is the source of truth for *what* is sent; this only manages
 * whether this device is reachable. Permission is only ever requested from an
 * explicit user gesture (`enable`) — a cold prompt is the fastest way to a
 * permanent "denied". Once granted, registration is refreshed silently on every
 * load so a rotated VAPID key or a re-installed PWA heals itself.
 */
export function usePushNotifications() {
  const { data: config, isLoading: configLoading } = usePushConfigQuery();
  const [subscribe] = useSubscribeMutation();
  const [unsubscribe] = useUnsubscribeMutation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subscribed, setSubscribed] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission | null>(
    typeof Notification !== 'undefined' ? Notification.permission : null,
  );

  const publicKey = config?.publicKey ?? null;
  const configEnabled = config?.enabled === true && publicKey != null;

  const register = useCallback(async (): Promise<boolean> => {
    if (!publicKey) return false;
    const sub = await ensureSubscription(publicKey);
    if (!sub) return false;
    const input = subscriptionInput(sub);
    if (!input) return false;
    await subscribe(input).unwrap();
    return true;
  }, [publicKey, subscribe]);

  // Heal on load: if the browser already granted permission, make sure the
  // server knows this device (a fresh install, a cleared subscription, a key
  // rotation). No prompt, no gesture needed.
  useEffect(() => {
    if (!configEnabled || typeof Notification === 'undefined') return;
    setPermission(Notification.permission);
    if (Notification.permission !== 'granted') return;
    let cancelled = false;
    void (async () => {
      try {
        const ok = await register();
        if (!cancelled && ok) setSubscribed(true);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'could not register for notifications');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [configEnabled, register]);

  /** Resolves to the browser's answer, or null when nothing was asked. */
  const enable = useCallback(async (): Promise<NotificationPermission | null> => {
    if (!configEnabled || typeof Notification === 'undefined') return null;
    setBusy(true);
    setError(null);
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === 'granted') setSubscribed(await register());
      return result;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not enable notifications');
      return null;
    } finally {
      setBusy(false);
    }
  }, [configEnabled, register]);

  const disable = useCallback(async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const sub = await currentSubscription();
      if (sub) {
        const input = subscriptionInput(sub);
        if (input) await unsubscribe({ endpoint: input.endpoint }).unwrap().catch(() => undefined);
        await sub.unsubscribe().catch(() => undefined);
      }
      setSubscribed(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not disable notifications');
    } finally {
      setBusy(false);
    }
  }, [unsubscribe]);

  const toggle = useCallback(async (): Promise<void> => {
    if (subscribed) await disable();
    else await enable();
  }, [subscribed, disable, enable]);

  const status: PushStatus = !pushSupported()
    ? 'unsupported'
    : !configEnabled
      ? 'disabled'
      : permission === 'denied'
        ? 'denied'
        : subscribed
          ? 'subscribed'
          : 'default';

  return { status, configLoading, busy, error, permission, subscribed, enable, disable, toggle };
}
